import {
  Collapse,
  Divider,
  ListItemButton,
  PopoverOrigin,
  Stack,
  useTheme,
} from "@mui/material";
import { CommonProps } from "@mui/material/OverridableComponent";
import React, { useMemo, useRef, useState } from "react";
import {
  Action,
  ActionGroup,
  isAction,
  isActionGroup,
  isSelectableItem,
  Item,
  ItemVariant,
  resolveActionGroupPreferences,
  SelectableItem,
  useResolveActionGroupColors,
} from "shared-ts-ui-action";
import { GroupNode } from "./GroupNode";
import { Layout } from "../../../Layout";
import { LayoutDirection } from "../../../LayoutDirection";
import { renderListItem } from "../list/renderListItem";
import { Menu } from "./Menu";

export interface GroupMenuProps extends CommonProps {
  variant?: ItemVariant;
  layout: Layout;
  layoutDirection?: LayoutDirection;
  item: ActionGroup;
  hideLabel?: boolean;
  hideSelected?: boolean;
  onSelected?: (item: SelectableItem) => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  disableTextInset?: boolean;
}

export const GroupMenu = (props: GroupMenuProps) => {
  const { layout, item } = props;
  const variant = props.variant ?? "rounded";
  const theme = useTheme();
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);
  const preferences = resolveActionGroupPreferences(item.preferences, layout);
  const hideSelected = props.hideSelected ?? preferences?.hideSelected ?? false;
  const layoutDirection = props.layoutDirection ?? preferences?.layoutDirection;
  const openOnHover = preferences?.openOnHover === true;
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = () => {
    if (closeTimeoutRef.current != null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
      closeTimeoutRef.current = null;
    }, 150);
  };

  const handleGroupNodeClick = () => {
    cancelScheduledClose();
    setOpen(true);
  };
  const handleClose = () => {
    cancelScheduledClose();
    setOpen(false);
  };
  const handleMouseEnter = () => {
    if (!openOnHover) return;
    cancelScheduledClose();
    setOpen(true);
  };
  const handleMouseLeave = () => {
    if (!openOnHover) return;
    scheduleClose();
  };

  const resolveSelectedItem = (items: Item[]): SelectableItem | undefined => {
    let selectedItem: SelectableItem | undefined;
    let action: Action | undefined;
    items.forEach((item) => {
      if (isSelectableItem(item) && item.selected) {
        selectedItem = item;

        if (isAction(item)) {
          action = item;
        }
      } else if (selectedItem === undefined && isActionGroup(item)) {
        selectedItem = resolveSelectedItem(item.items);
      }
    });

    if (action) {
      action.onExecute = handleGroupNodeClick;
    }
    return selectedItem;
  };

  const selectedItem = useMemo(
    () => resolveSelectedItem(item.items),
    [item.items],
  );

  const actionGroupColors = useResolveActionGroupColors();

  const handleItemSelected = (item: SelectableItem) => {
    props.onSelected?.(item);
    setOpen(false);
  };

  return (
    <Stack
      ref={anchorRef}
      className={`Item GroupMenu layout-${layout} variant-${variant}`}
      direction={"column"}
      onMouseEnter={openOnHover ? handleMouseEnter : undefined}
      onMouseLeave={openOnHover ? handleMouseLeave : undefined}
      sx={{
        "&.layout-large": {
          borderRadius:
            hideSelected || selectedItem != null ? "9px" : undefined,
          borderStyle: "solid",
          borderWidth: 2,
          borderColor: actionGroupColors.primary,
        },
        "&.layout-medium": {
          borderRadius: "9px",
          borderStyle: "solid",
          borderWidth: 2,
          borderColor: actionGroupColors.primary,
          "&.direction-column": {
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
        },
        "&.variant-rounded": {
          ">.Item": {
            borderTopLeftRadius: "9px",
            borderTopRightRadius: "9px",
            borderBottomRightRadius: hideSelected ? "9px" : 0,
            borderBottomLeftRadius: hideSelected ? "9px" : 0,
          },
        },
      }}
    >
      <GroupNode
        variant={variant}
        item={props.item}
        open={open}
        layout={layout}
        hideLabel={props.hideLabel}
        hasSiblings={selectedItem != null && !hideSelected}
        onClick={handleGroupNodeClick}
      />
      <Menu
        open={open}
        variant={variant}
        layout={layout}
        layoutDirection={layoutDirection}
        item={item}
        anchorOrigin={props.anchorOrigin}
        transformOrigin={props.transformOrigin}
        anchorRef={anchorRef}
        onClose={handleClose}
        onItemSelected={handleItemSelected}
        onPaperMouseEnter={openOnHover ? handleMouseEnter : undefined}
        onPaperMouseLeave={openOnHover ? handleMouseLeave : undefined}
      />
      {!hideSelected && (
        <Collapse
          className={"GroupSelectedItemContainer layout-" + layout}
          in={selectedItem != null}
          timeout={{ enter: 500, exit: 500 }}
          sx={{
            "&.layout-medium": {
              ".Item": {
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
              },
            },
            ".Item": {
              borderBottomLeftRadius: "9px",
              borderBottomRightRadius: "9px",
              "&.selected": {
                backgroundColor: "unset",
                ".MuiButtonBase-root": {
                  backgroundColor: "unset",
                },
                "&:hover": {
                  backgroundColor: "unset",
                  cursor: "auto",
                },
                ".CheckIcon": {
                  display: "none",
                },
              },
            },
          }}
        >
          {selectedItem != null && (
            <>
              <Divider sx={{ ml: 1, mr: 1 }} />
              {renderListItem({
                variant: "square",
                layout: layout,
                item: selectedItem,
                hideLabels: false,
              })}
            </>
          )}
          {selectedItem == null && (
            <>
              <Divider />
              <ListItemButton
                className={"Item NavigationItem layout-" + layout}
                sx={{ minHeight: "66px" }}
              ></ListItemButton>
            </>
          )}
        </Collapse>
      )}
    </Stack>
  );
};
